import { AttachFile, Refresh, Send } from "@mui/icons-material";
import { Button, Chip, CircularProgress, IconButton, styled, TextField, useTheme } from "@mui/material";
import { Box } from "@mui/material";
import { ConversationView, Message } from "@my-helpers/common";
import { ElementType, useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";


const FileManagementArea = styled(Box)(({ theme }) => ({
    padding: `0 ${theme.spacing(2)}`,
    backgroundColor: theme.palette.grey[50],
    minHeight: '40px',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
}));

const InputArea = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'flex-end',
    flexShrink: 0
}));

interface UserInputProps {
    isLoading?: string;
    messageStatus?: string;
    currentConversation?: ConversationView;
    selectedParticipant?: string;
    startConversation: (participantId?: string, topic?: string) => Promise<string | null>
    submitMessage: (message: Message) => Promise<boolean>;
    setExpandedThinkBlocks: (blocks: Set<string>) => void;
}

const UserInput: ElementType<UserInputProps> = ({
    isLoading,
    messageStatus,
    currentConversation,
    selectedParticipant,
    startConversation,
    submitMessage,
    setExpandedThinkBlocks
}) => {
    
    const theme = useTheme();
    const navigate = useNavigate();
    
    const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [inputValue, setInputValue] = useState('');

    const getPlaceholderText = (): string => {
        if (isLoading) return isLoading;
        if (messageStatus && messageStatus !== 'received') return messageStatus + "...";
        return "Type your message (Shift+Enter for new line)...";
    };

    const handleResetChat = () => {
        setInputValue('');
        setAttachedFiles([]);
        setExpandedThinkBlocks(new Set());
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files || []);
        setAttachedFiles(prev => [...prev, ...files]);
    };

    const removeFile = (index: number) => {
        setAttachedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleSendMessage = useCallback(async () => {
        if (!inputValue.trim()) return;
        if (isLoading !== undefined) return;

        if (!currentConversation && selectedParticipant) {
            const id = await startConversation(selectedParticipant, inputValue);
            if (id) {
                navigate(`/${id}`);
            }
            return;
        } else if (currentConversation && currentConversation.currentThread) {
            const newMessage: Message = {
                role: 'user',
                content: inputValue
            };

            await submitMessage(newMessage);
            // Clear input and files
            setInputValue('');
            setAttachedFiles([]);
        } else {
            console.error('No current conversation or thread found');
        }
    }, [
        selectedParticipant,
        inputValue,
        isLoading,
        currentConversation,
        setInputValue,
        setAttachedFiles,
        navigate,
        submitMessage,
        startConversation
    ]);

    const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handleSendMessage();
        }
    }, [handleSendMessage]);

    return (
        <Box 
            display="flex" 
            flexDirection="column" 
            borderTop={`1px solid ${theme.palette.divider}`}
        >
            {attachedFiles.length > 0 && (
                <FileManagementArea>
                    {attachedFiles.map((file, index) => (
                        <Chip
                            key={index}
                            label={file.name}
                            onDelete={() => removeFile(index)}
                            size="small"
                            variant="outlined"
                        />
                    ))}
                </FileManagementArea>
            )}
            <InputArea>
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    style={{ display: 'none' }}
                    accept=".txt,.md,.json,.csv,.py,.js,.html,.css,.xml,text/*,application/pdf,image/*"
                    onChange={handleFileSelect}
                />

                <IconButton
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isLoading !== undefined}
                    size="small"
                >
                    <AttachFile />
                </IconButton>

                <TextField
                    multiline
                    maxRows={4}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={getPlaceholderText()}
                    disabled={isLoading !== undefined}
                    sx={{ flexGrow: 1 }}
                    size="small"
                />

                <Button
                    variant="contained"
                    onClick={handleSendMessage}
                    disabled={(!inputValue.trim() && attachedFiles.length === 0) || isLoading !== undefined}
                    startIcon={isLoading!==undefined ? <CircularProgress size={16} /> : <Send />}
                    size="small"
                >
                    Send
                </Button>

                <Button
                    variant="outlined"
                    onClick={handleResetChat}
                    disabled={isLoading !== undefined}
                    startIcon={<Refresh />}
                    size="small"
                >
                    Reset
                </Button>
            </InputArea>
        </Box>
    )
}

export default UserInput;